/**
 * AdminSystemConfig page — /admin/system-config
 *
 * Central admin page for runtime system configuration.
 * Currently contains: DigiPART Environment selector.
 * Future sections can be appended below the DigiPART card.
 */

import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import { Settings2, ChevronLeft } from 'lucide-react';
import { DigipartEnvironmentCard } from '../components/DigipartEnvironmentCard';
import { theme } from '../styles/theme';

const AdminSystemConfig: React.FC = () => {
  const { user } = useAuth();

  if (!user?.is_superuser) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-6">
        <Link
          to="/admin"
          className={`flex items-center gap-1 text-sm ${theme.text.secondary} hover:${theme.text.primary} transition-colors`}
        >
          <ChevronLeft className="w-4 h-4" />
          Admin
        </Link>
        <span className={`text-sm ${theme.text.tertiary}`}>/</span>
        <span className={`text-sm ${theme.text.primary} font-medium`}>System Configuration</span>
      </div>

      {/* Page header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="p-2 rounded-lg bg-interactive-primary/10">
          <Settings2 className="w-6 h-6 text-interactive-primary" />
        </div>
        <div>
          <h2 className={`text-2xl font-bold ${theme.text.primary}`}>System Configuration</h2>
          <p className={`text-sm ${theme.text.secondary} mt-0.5`}>
            Runtime settings — changes take effect immediately without redeployment.
          </p>
        </div>
      </div>

      {/* Configuration sections */}
      <div className="space-y-6">
        {/* DigiPART environment */}
        <section aria-labelledby="digipart-section-heading">
          <h2
            id="digipart-section-heading"
            className={`text-sm font-semibold uppercase tracking-wide ${theme.text.tertiary} mb-3`}
          >
            DigiPART Integration
          </h2>
          <DigipartEnvironmentCard />
        </section>
      </div>
    </div>
  );
};

export default AdminSystemConfig;
